/**
 * JobDetail Component
 * Shows full job details with apply functionality
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  TextField,
  IconButton,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import BadgeIcon from '@mui/icons-material/Badge';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Job } from '@services/jobsService';

interface JobDetailProps {
  job: Job;
  open: boolean;
  onClose: () => void;
  onApply: (job: Job, message?: string) => void;
  hasApplied: boolean;
}

export function JobDetail({ job, open, onClose, onApply, hasApplied }: JobDetailProps) {
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    setApplying(true);
    await onApply(job, applicationMessage);
    setApplying(false);
    setShowApplyForm(false);
    setApplicationMessage('');
  };

  // Format salary display
  const formatSalary = () => {
    if (job.payRange) return job.payRange;
    if (job.salary_min && job.salary_max) {
      const type = job.salary_type === 'hourly' ? '/hr' : job.salary_type === 'daily' ? '/day' : '/yr';
      return `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}${type}`;
    }
    if (job.salary_min) {
      const type = job.salary_type === 'hourly' ? '/hr' : job.salary_type === 'daily' ? '/day' : '/yr';
      return `From $${job.salary_min.toLocaleString()}${type}`;
    }
    return 'Competitive';
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e293b',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ flex: 1, pr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {job.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <BusinessIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {job.company}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {/* Quick Info */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5, 
          mb: 3,
          p: 2,
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {job.location || job.county || job.basin || 'Location TBD'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WorkIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              {job.jobType || 'Full-time'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AttachMoneyIcon sx={{ color: '#22c55e', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
              {formatSalary()}
            </Typography>
          </Box>
        </Box>

        {/* Has Applied Banner */}
        {hasApplied && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />}
            sx={{ 
              mb: 2, 
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              '& .MuiAlert-icon': { color: '#22c55e' }
            }}
          >
            You've already applied to this job
          </Alert>
        )}

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: '#3b82f6', mb: 1, fontWeight: 600 }}>
            Job Description
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.8)',
              whiteSpace: 'pre-line',
              lineHeight: 1.7
            }}
          >
            {job.description}
          </Typography>
        </Box>

        {/* Required Certifications */}
        {job.required_certifications && job.required_certifications.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#3b82f6', mb: 1, fontWeight: 600 }}>
              Required Certifications
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {job.required_certifications.map((cert, i) => (
                <Chip 
                  key={i}
                  icon={<BadgeIcon />}
                  label={cert}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    color: 'rgba(245, 158, 11, 0.9)',
                    '& .MuiChip-icon': { color: 'rgba(245, 158, 11, 0.7)' }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Experience Required */}
        {job.required_experience_years && job.required_experience_years > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#3b82f6', mb: 1, fontWeight: 600 }}>
              Experience Required
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {job.required_experience_years}+ years
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

        {/* Contact Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#3b82f6', mb: 1, fontWeight: 600 }}>
            Contact Information
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.8)',
              whiteSpace: 'pre-line'
            }}
          >
            {job.contactInfo || 'Apply through the app'}
          </Typography>
        </Box>

        {/* Posted Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
          <AccessTimeIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }} />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Posted: {formatDate(job.createdAt)} • Expires: {formatDate(job.expiresAt)}
          </Typography>
        </Box>

        {/* Application Form */}
        {showApplyForm && !hasApplied && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: 'white', mb: 1 }}>
              Include a message (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Tell them why you're a great fit..."
              value={applicationMessage}
              onChange={(e) => setApplicationMessage(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  '& textarea': { color: 'white' }
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setShowApplyForm(false)}
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'white'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={applying}
                startIcon={<SendIcon />}
                sx={{ 
                  backgroundColor: '#22c55e',
                  '&:hover': { backgroundColor: '#16a34a' }
                }}
              >
                {applying ? 'Submitting...' : 'Submit Application'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Close
        </Button>
        {!hasApplied && !showApplyForm && (
          <Button
            variant="contained"
            onClick={() => setShowApplyForm(true)}
            startIcon={<SendIcon />}
            sx={{ 
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' }
            }}
          >
            Apply Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default JobDetail;
